import { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { Users2, LayoutDashboard, Briefcase, KanbanSquare, Loader2, Plus, X, Trash2, Upload, Download, Star, Megaphone, Pause, Play, Check } from "lucide-react";
import { toast } from "sonner";
import OSLayout from "../../components/os/OSLayout";
import { api } from "../../lib/api";
import { useI18n } from "../../context/I18nContext";
import { useAuth } from "../../context/AuthContext";

const ease = [0.16, 1, 0.3, 1];
const LEADERSHIP = ["admin", "ceo", "director"];
const STAGES = ["nuevo", "preseleccion", "entrevista", "oferta", "contratado", "rechazado"];

function Modal({ title, onClose, children, testid, wide }) {
  useEffect(() => {
    const h = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [onClose]);
  return (
    <div className="fixed inset-0 z-50 bg-navy/40 backdrop-blur-sm flex items-center justify-center p-4" data-testid={testid}>
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, ease }}
        className={`card-surface w-full ${wide ? "max-w-2xl" : "max-w-md"} max-h-[90vh] overflow-y-auto p-6`}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-editorial text-2xl">{title}</h3>
          <button onClick={onClose} className="text-navy/40 hover:text-navy"><X className="w-5 h-5" /></button>
        </div>
        {children}
      </motion.div>
    </div>
  );
}

/* ============ PANEL ============ */
function DashboardTab() {
  const { t } = useI18n();
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/talent/dashboard").then((r) => setD(r.data)).catch(() => {}); }, []);
  if (!d) return <div className="py-16 flex justify-center text-navy/40"><Loader2 className="w-6 h-6 animate-spin" /></div>;
  const kpis = [
    { label: t("os.talent.openVacancies"), value: d.open_vacancies }, { label: t("os.talent.totalCandidates"), value: d.total_candidates },
    { label: t("os.talent.hired"), value: d.hired }, { label: t("os.talent.conversion"), value: `${d.conversion}%` },
    { label: t("os.talent.avgRating"), value: d.avg_rating || "—" },
  ];
  const max = Math.max(1, ...d.by_stage.map((s) => s.count));
  return (
    <div className="space-y-8" data-testid="talent-dashboard">
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        {kpis.map((k, i) => (
          <div key={k.label} className="card-surface p-5" data-testid={`talent-dash-kpi-${i}`}>
            <div className="overline text-navy/40">{k.label}</div>
            <div className="mt-3 text-3xl font-medium tracking-tight">{k.value}</div>
          </div>
        ))}
      </div>
      <div className="card-surface p-6">
        <div className="overline text-navy/40 mb-4">{t("os.talent.byStage")}</div>
        <div className="space-y-2">
          {d.by_stage.map((s) => (
            <div key={s.stage} className="flex items-center gap-3">
              <div className="w-28 text-sm text-navy/60 shrink-0">{t(`os.talent.stage.${s.stage}`)}</div>
              <div className="flex-1 h-2.5 rounded-full bg-navy/5 overflow-hidden">
                <div className="h-full bg-gradient-violet" style={{ width: `${(s.count / max) * 100}%` }} />
              </div>
              <div className="w-8 text-right text-sm text-navy/60">{s.count}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ============ VACANTES ============ */
function VacancyDialog({ meta, onClose, onSaved }) {
  const { t } = useI18n();
  const [f, setF] = useState({ title: "", department: "", position_id: "", employment_type: "", location: "", description: "", openings: 1 });
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!f.title.trim()) return toast.error(t("os.talent.vacancyTitle"));
    setSaving(true);
    try {
      await api.post("/talent/vacancies", { ...f, openings: Number(f.openings) || 1, position_id: f.position_id || null, employment_type: f.employment_type || null });
      toast.success(t("os.talent.vacancyCreated")); onSaved(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); } finally { setSaving(false); }
  };
  return (
    <Modal title={t("os.talent.newVacancy")} onClose={onClose} testid="talent-vacancy-dialog">
      <div className="space-y-3">
        <input className="field" placeholder={t("os.talent.vacancyTitle")} value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="talent-vacancy-title" />
        <input className="field" placeholder={t("os.talent.department")} value={f.department} onChange={(e) => setF({ ...f, department: e.target.value })} />
        <select className="field" value={f.position_id} onChange={(e) => setF({ ...f, position_id: e.target.value })}>
          <option value="">{t("os.talent.position")}</option>
          {(meta.positions || []).map((p) => <option key={p.id} value={p.id}>{p.title}</option>)}
        </select>
        <select className="field" value={f.employment_type} onChange={(e) => setF({ ...f, employment_type: e.target.value })}>
          <option value="">{t("os.talent.employmentType")}</option>
          {meta.employment_types.map((x) => <option key={x} value={x}>{t(`os.talent.empType.${x}`)}</option>)}
        </select>
        <input className="field" placeholder={t("os.talent.location")} value={f.location} onChange={(e) => setF({ ...f, location: e.target.value })} />
        <textarea className="field" rows={3} placeholder={t("os.talent.description")} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} />
        <input className="field" type="number" placeholder={t("os.talent.openings")} value={f.openings} onChange={(e) => setF({ ...f, openings: e.target.value })} />
      </div>
      <div className="flex justify-end gap-2 mt-5">
        <button onClick={onClose} className="btn btn-ghost">{t("os.talent.cancel")}</button>
        <button onClick={submit} disabled={saving} className="btn btn-primary" data-testid="talent-vacancy-submit">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : t("os.talent.create")}</button>
      </div>
    </Modal>
  );
}

function VacanciesTab({ meta, isLeader }) {
  const { t } = useI18n();
  const [vacs, setVacs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get("/talent/vacancies"); setVacs(r.data); } catch { /* noop */ } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);
  const publish = async (id) => { try { await api.post(`/talent/vacancies/${id}/publish`); toast.success(t("os.talent.published")); load(); } catch (e) { toast.error(e?.response?.data?.detail || "Error"); } };
  const setStatus = async (id, status) => { try { await api.post(`/talent/vacancies/${id}/status`, { status }); load(); } catch (e) { toast.error(e?.response?.data?.detail || "Error"); } };
  const del = async (id) => { try { await api.delete(`/talent/vacancies/${id}`); load(); } catch (e) { toast.error(e?.response?.data?.detail || "Error"); } };
  const badge = (s) => s === "abierta" ? "badge-emerald" : s === "borrador" ? "badge-muted" : s === "pausada" ? "badge-amber" : "badge-danger";
  return (
    <div className="space-y-5" data-testid="talent-vacancies">
      {isLeader && <div className="flex justify-end"><button onClick={() => setOpen(true)} className="btn btn-primary" data-testid="talent-new-vacancy-btn"><Plus className="w-4 h-4" /> {t("os.talent.newVacancy")}</button></div>}
      {loading ? <div className="py-16 flex justify-center text-navy/40"><Loader2 className="w-6 h-6 animate-spin" /></div>
        : vacs.length === 0 ? <div className="card-surface py-16 text-center text-navy/40 text-sm">{t("os.talent.noVacancies")}</div>
        : (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="talent-vacancies-grid">
            {vacs.map((v) => (
              <div key={v.id} className="card-surface p-4" data-testid={`talent-vacancy-${v.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0"><div className="font-medium">{v.title}</div><div className="text-xs text-navy/50">{v.department}{v.location ? ` · ${v.location}` : ""}</div></div>
                  <span className={`badge ${badge(v.status)}`}>{t(`os.talent.vacancyStatus.${v.status}`)}</span>
                </div>
                {v.description && <p className="text-xs text-navy/50 mt-2 line-clamp-2">{v.description}</p>}
                <div className="mt-2 text-[11px] text-navy/40">{v.candidate_count} {t("os.talent.candidates")} · {v.openings} {t("os.talent.openings")}</div>
                {isLeader && (
                  <div className="mt-3 flex flex-wrap items-center gap-2">
                    {v.status === "borrador" && <button onClick={() => publish(v.id)} className="btn btn-outline text-xs" data-testid={`talent-publish-${v.id}`}><Megaphone className="w-3.5 h-3.5" /> {t("os.talent.publish")}</button>}
                    {v.status === "abierta" && <button onClick={() => setStatus(v.id, "pausada")} className="btn btn-ghost text-xs"><Pause className="w-3.5 h-3.5" /> {t("os.talent.pause")}</button>}
                    {(v.status === "pausada" || v.status === "cerrada") && <button onClick={() => setStatus(v.id, "abierta")} className="btn btn-ghost text-xs"><Play className="w-3.5 h-3.5" /> {t("os.talent.reopen")}</button>}
                    {v.status !== "cerrada" && <button onClick={() => setStatus(v.id, "cerrada")} className="btn btn-ghost text-xs">{t("os.talent.closeVac")}</button>}
                    <button onClick={() => del(v.id)} className="text-navy/30 hover:text-destructive ml-auto"><Trash2 className="w-4 h-4" /></button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      {open && <VacancyDialog meta={meta} onClose={() => setOpen(false)} onSaved={load} />}
    </div>
  );
}

/* ============ CANDIDATO (detalle) ============ */
function CandidateDetail({ cid, meta, onClose, onChanged }) {
  const { t } = useI18n();
  const [c, setC] = useState(null);
  const [ev, setEv] = useState({ score: 4, recommendation: "quizas", comments: "" });
  const load = useCallback(async () => { try { const r = await api.get(`/talent/candidates/${cid}`); setC(r.data); } catch { /* noop */ } }, [cid]);
  useEffect(() => { load(); }, [load]);
  const uploadCv = async (file) => {
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try { await api.post(`/talent/candidates/${cid}/cv`, fd, { headers: { "Content-Type": "multipart/form-data" } }); toast.success("CV"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };
  const dlCv = async () => {
    try { const r = await api.get(`/talent/candidates/${cid}/cv`, { responseType: "blob" }); const u = URL.createObjectURL(r.data); const a = document.createElement("a"); a.href = u; a.download = c.cv_filename || "cv"; a.click(); URL.revokeObjectURL(u); }
    catch { toast.error("Error"); }
  };
  const addEval = async () => {
    try { await api.post(`/talent/candidates/${cid}/evaluations`, ev); toast.success(t("os.talent.addEvaluation")); setEv({ score: 4, recommendation: "quizas", comments: "" }); load(); onChanged && onChanged(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };
  if (!c) return <Modal title="…" onClose={onClose} testid="talent-candidate-detail"><div className="py-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-navy/40" /></div></Modal>;
  return (
    <Modal title={c.name} onClose={onClose} testid="talent-candidate-detail" wide>
      <div className="space-y-4">
        <div className="flex flex-wrap gap-2 text-sm text-navy/60">
          <span>{c.email || "—"}</span><span>·</span><span>{c.phone || "—"}</span><span>·</span>
          <span className="badge badge-violet">{t(`os.talent.stage.${c.stage}`)}</span>
          <span className="badge badge-muted">{t(`os.talent.src.${c.source}`)}</span>
          {c.rating ? <span className="flex items-center gap-1 text-gold"><Star className="w-3.5 h-3.5 fill-current" /> {c.rating}</span> : null}
        </div>
        {c.notes && <p className="text-sm text-navy/70">{c.notes}</p>}
        <div className="flex items-center gap-2">
          {c.has_cv ? <button onClick={dlCv} className="btn btn-ghost text-sm" data-testid="talent-cand-dl-cv"><Download className="w-4 h-4" /> {t("os.talent.downloadCv")}</button> : null}
          <label className="btn btn-outline text-sm cursor-pointer" data-testid="talent-cand-upload-cv"><Upload className="w-4 h-4" /> {t("os.talent.uploadCv")}
            <input type="file" className="hidden" onChange={(e) => uploadCv(e.target.files[0])} /></label>
        </div>

        <div className="border-t border-navy/10 pt-4">
          <div className="overline text-navy/40 mb-3">{t("os.talent.evaluations")} ({c.evaluations?.length || 0})</div>
          <div className="space-y-2 mb-4">
            {(c.evaluations || []).map((e) => (
              <div key={e.id} className="card-surface p-3">
                <div className="flex items-center justify-between text-sm">
                  <span className="font-medium">{e.interviewer_name}</span>
                  <span className="flex items-center gap-2"><span className="text-gold flex items-center gap-0.5"><Star className="w-3.5 h-3.5 fill-current" />{e.score}</span><span className="badge badge-muted">{t(`os.talent.rec.${e.recommendation}`)}</span></span>
                </div>
                {e.comments && <p className="text-xs text-navy/60 mt-1">{e.comments}</p>}
              </div>
            ))}
          </div>
          <div className="grid sm:grid-cols-3 gap-2">
            <select className="field-sm" value={ev.score} onChange={(e) => setEv({ ...ev, score: Number(e.target.value) })} data-testid="talent-eval-score">
              {[1, 2, 3, 4, 5].map((n) => <option key={n} value={n}>{n} / 5</option>)}
            </select>
            <select className="field-sm" value={ev.recommendation} onChange={(e) => setEv({ ...ev, recommendation: e.target.value })}>
              {meta.recommendations.map((r) => <option key={r} value={r}>{t(`os.talent.rec.${r}`)}</option>)}
            </select>
            <button onClick={addEval} className="btn btn-primary text-sm" data-testid="talent-eval-add"><Plus className="w-4 h-4" /> {t("os.talent.addEvaluation")}</button>
          </div>
          <input className="field mt-2" placeholder={t("os.talent.comments")} value={ev.comments} onChange={(e) => setEv({ ...ev, comments: e.target.value })} data-testid="talent-eval-comments" />
        </div>
      </div>
    </Modal>
  );
}

/* ============ PIPELINE (kanban) ============ */
function HireModal({ cand, meta, onClose, onHired }) {
  const { t } = useI18n();
  const [userId, setUserId] = useState(cand.linked_user_id || "");
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!userId) return toast.error(t("os.talent.selectUser"));
    setSaving(true);
    try { await api.post(`/talent/candidates/${cand.id}/stage`, { stage: "contratado", linked_user_id: userId }); toast.success(t("os.talent.hire")); onHired(); onClose(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Error"); } finally { setSaving(false); }
  };
  return (
    <Modal title={t("os.talent.hireTitle")} onClose={onClose} testid="talent-hire-modal">
      <p className="text-sm text-navy/55 mb-3">{t("os.talent.linkUserHelp")}</p>
      <select className="field" value={userId} onChange={(e) => setUserId(e.target.value)} data-testid="talent-hire-user">
        <option value="">{t("os.talent.selectUser")}</option>
        {(meta.users || []).map((u) => <option key={u.id} value={u.id}>{u.name} ({u.email})</option>)}
      </select>
      <div className="flex justify-end gap-2 mt-5">
        <button onClick={onClose} className="btn btn-ghost">{t("os.talent.cancel")}</button>
        <button onClick={submit} disabled={saving} className="btn btn-primary" data-testid="talent-hire-confirm">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : t("os.talent.hire")}</button>
      </div>
    </Modal>
  );
}

function CandidateDialog({ vacancyId, meta, onClose, onSaved }) {
  const { t } = useI18n();
  const [f, setF] = useState({ vacancy_id: vacancyId, name: "", email: "", phone: "", source: "otro", notes: "" });
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!f.name.trim()) return toast.error(t("os.talent.candidateName"));
    setSaving(true);
    try { await api.post("/talent/candidates", f); toast.success(t("os.talent.candidateAdded")); onSaved(); onClose(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Error"); } finally { setSaving(false); }
  };
  return (
    <Modal title={t("os.talent.newCandidate")} onClose={onClose} testid="talent-candidate-dialog">
      <div className="space-y-3">
        <input className="field" placeholder={t("os.talent.candidateName")} value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="talent-cand-name" />
        <input className="field" placeholder={t("os.talent.email")} value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="talent-cand-email" />
        <input className="field" placeholder={t("os.talent.phone")} value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} />
        <select className="field" value={f.source} onChange={(e) => setF({ ...f, source: e.target.value })}>
          {meta.sources.map((s) => <option key={s} value={s}>{t(`os.talent.src.${s}`)}</option>)}
        </select>
        <textarea className="field" rows={2} placeholder={t("os.talent.notes")} value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} />
      </div>
      <div className="flex justify-end gap-2 mt-5">
        <button onClick={onClose} className="btn btn-ghost">{t("os.talent.cancel")}</button>
        <button onClick={submit} disabled={saving} className="btn btn-primary" data-testid="talent-cand-submit">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : t("os.talent.create")}</button>
      </div>
    </Modal>
  );
}

function PipelineTab({ meta }) {
  const { t } = useI18n();
  const [vacs, setVacs] = useState([]);
  const [vid, setVid] = useState("");
  const [cands, setCands] = useState([]);
  const [loading, setLoading] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [hire, setHire] = useState(null);
  useEffect(() => { api.get("/talent/vacancies").then((r) => { setVacs(r.data); if (r.data[0]) setVid(r.data[0].id); }).catch(() => {}); }, []);
  const load = useCallback(async () => {
    if (!vid) return;
    setLoading(true);
    try { const r = await api.get("/talent/candidates", { params: { vacancy_id: vid } }); setCands(r.data); } catch { /* noop */ } finally { setLoading(false); }
  }, [vid]);
  useEffect(() => { load(); }, [load]);
  const move = async (cand, stage) => {
    if (stage === "contratado") { setHire(cand); return; }
    try { await api.post(`/talent/candidates/${cand.id}/stage`, { stage }); load(); } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };
  const del = async (id) => { try { await api.delete(`/talent/candidates/${id}`); load(); } catch (e) { toast.error(e?.response?.data?.detail || "Error"); } };
  return (
    <div className="space-y-5" data-testid="talent-pipeline">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <select className="field max-w-xs" value={vid} onChange={(e) => setVid(e.target.value)} data-testid="talent-pipeline-vacancy">
          <option value="">{t("os.talent.selectVacancy")}</option>
          {vacs.map((v) => <option key={v.id} value={v.id}>{v.title}</option>)}
        </select>
        {vid && <button onClick={() => setAddOpen(true)} className="btn btn-primary" data-testid="talent-add-candidate-btn"><Plus className="w-4 h-4" /> {t("os.talent.newCandidate")}</button>}
      </div>
      {!vid ? <div className="card-surface py-16 text-center text-navy/40 text-sm">{t("os.talent.selectVacancy")}</div>
        : loading ? <div className="py-16 flex justify-center text-navy/40"><Loader2 className="w-6 h-6 animate-spin" /></div>
        : (
          <div className="grid gap-3 md:grid-cols-3 xl:grid-cols-6" data-testid="talent-kanban">
            {STAGES.map((s) => {
              const col = cands.filter((c) => c.stage === s);
              return (
                <div key={s} className="bg-navy/[0.03] rounded-lg p-2.5 min-h-[120px]" data-testid={`talent-col-${s}`}>
                  <div className="text-xs font-medium text-navy/55 px-1 mb-2 flex items-center justify-between">
                    <span>{t(`os.talent.stage.${s}`)}</span><span className="badge badge-muted">{col.length}</span>
                  </div>
                  <div className="space-y-2">
                    {col.map((c) => (
                      <div key={c.id} className="card-surface p-3" data-testid={`talent-card-${c.id}`}>
                        <button onClick={() => setDetail(c.id)} className="text-left w-full" data-testid={`talent-card-open-${c.id}`}>
                          <div className="font-medium text-sm truncate">{c.name}</div>
                          <div className="text-[11px] text-navy/45 truncate">{c.email || t(`os.talent.src.${c.source}`)}</div>
                          <div className="mt-1 flex items-center gap-2 text-[11px] text-navy/50">
                            {c.rating ? <span className="flex items-center gap-0.5 text-gold"><Star className="w-3 h-3 fill-current" />{c.rating}</span> : null}
                            {c.evaluation_count ? <span>· {c.evaluation_count} eval</span> : null}
                            {c.has_cv ? <span>· CV</span> : null}
                          </div>
                        </button>
                        <div className="mt-2 flex items-center gap-1">
                          <select className="field-sm text-xs flex-1" value={c.stage} onChange={(e) => move(c, e.target.value)} data-testid={`talent-move-${c.id}`}>
                            {STAGES.map((st) => <option key={st} value={st}>{t(`os.talent.stage.${st}`)}</option>)}
                          </select>
                          <button onClick={() => del(c.id)} className="text-navy/25 hover:text-destructive"><Trash2 className="w-3.5 h-3.5" /></button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      {addOpen && <CandidateDialog vacancyId={vid} meta={meta} onClose={() => setAddOpen(false)} onSaved={load} />}
      {detail && <CandidateDetail cid={detail} meta={meta} onClose={() => setDetail(null)} onChanged={load} />}
      {hire && <HireModal cand={hire} meta={meta} onClose={() => setHire(null)} onHired={load} />}
    </div>
  );
}

/* ============ STUDIO ============ */
export default function TalentStudio() {
  const { t } = useI18n();
  const { user } = useAuth();
  const isLeader = LEADERSHIP.includes(user?.role);
  const [meta, setMeta] = useState(null);
  const [tab, setTab] = useState(isLeader ? "dashboard" : "vacancies");
  useEffect(() => { api.get("/talent/meta").then((r) => setMeta(r.data)).catch(() => {}); }, []);

  const leaderTabs = [
    { key: "dashboard", label: t("os.talent.tabs.dashboard"), icon: LayoutDashboard },
    { key: "vacancies", label: t("os.talent.tabs.vacancies"), icon: Briefcase },
    { key: "pipeline", label: t("os.talent.tabs.pipeline"), icon: KanbanSquare },
  ];
  const tabs = isLeader ? leaderTabs : [{ key: "vacancies", label: t("os.talent.tabs.vacancies"), icon: Briefcase }];

  return (
    <OSLayout title={t("os.talent.title")}>
      <div className="space-y-8" data-testid="talent-page">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease }}>
          <div className="overline text-navy/40 mb-2 flex items-center gap-2"><Users2 className="w-3.5 h-3.5" /> {t("os.talent.overline")}</div>
          <h2 className="font-editorial text-4xl md:text-5xl tracking-tight">{t("os.talent.title")}</h2>
          <p className="mt-2 text-navy/50 max-w-2xl">{t("os.talent.subtitle")}</p>
        </motion.div>

        <div className="flex flex-wrap gap-2 border-b border-navy/10 pb-1">
          {tabs.map((tb) => (
            <button key={tb.key} onClick={() => setTab(tb.key)} data-testid={`talent-tab-${tb.key}`}
              className={`inline-flex items-center gap-2 px-3.5 py-2 rounded-full text-sm transition-colors ${tab === tb.key ? "bg-gradient-violet text-white" : "text-navy/55 hover:text-navy hover:bg-navy/5"}`}>
              <tb.icon className="w-3.5 h-3.5" /> {tb.label}
            </button>
          ))}
        </div>

        {!meta ? <div className="py-16 flex justify-center text-navy/40"><Loader2 className="w-6 h-6 animate-spin" /></div> : (
          <>
            {tab === "dashboard" && isLeader && <DashboardTab />}
            {tab === "vacancies" && <VacanciesTab meta={meta} isLeader={isLeader} />}
            {tab === "pipeline" && isLeader && <PipelineTab meta={meta} />}
          </>
        )}
      </div>
    </OSLayout>
  );
}
